import React, { useMemo, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import {
  CellLeftV8,
  order2Sorting,
  TableCellV8,
  TablePaginatedV8,
  CellYesNoV8,
  EyeColumnV8,
} from 'motion-components';
import { useLocation } from 'react-router-dom';
import { generatePath, useHistory } from 'react-router';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  isActiveColumn,
  lastDateUserColumn,
} from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    dataCount,
  } = props;

  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('JobtypesForm'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'code',
        header: t('Κωδικός'),
        accessorKey: 'code',
        orderByField: 'code',
        columnName: t('Κωδικός'),
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 20,
      },
      {
        id: 'description',
        header: t('Περιγραφή'),
        accessorKey: 'description',
        orderByField: 'description',
        columnName: t('Περιγραφή'),
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 30,
      },
      {
        id: 'detailedDescription',
        header: t('Αναλυτική Περιγραφή'),
        accessorKey: 'detailedDescription',
        orderByField: 'detailedDescription',
        columnName: t('Αναλυτική Περιγραφή'),
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 40,
      },
      {
        id: 'chargingCode',
        header: t('Κωδικός Χρέωσης'),
        accessorKey: 'chargingCode',
        orderByField: 'chargingCode',
        columnName: t('Κωδικός Χρέωσης'),
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 25,
      },
      {
        id: 'material.name',
        header: t('Υλικό'),
        accessorKey: 'material.name',
        columnName: t('Υλικό'),
        cell: ({ row }) => {
          const materialName = row.original.material?.name ?? '';
          return <span>{materialName}</span>;
        },
        size: 30,
      },
      isActiveColumn(t, i18n),
      firstDateUserColumn(t),
      lastDateUserColumn(t),
    ],
    [t, i18n.language],
  );

  const tableRef = useRef(null);
  useImperativeHandle(ref, () => tableRef.current || null);

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort, columnDef, orderBy) => {
            onOrderChange(orderBy);
          }}
          topBorderRadius={false}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
